import { FieldArray } from 'react-final-form-arrays';
import { Fragment, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useField } from 'react-final-form';

import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { Spinner } from 'UIPackage/MainModule/components/Spinner';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { ErknmActViolationItem } from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmActViolationItem';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ErknmActViolationInitItem } from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmActViolationInitItem';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { ViolationItem } from 'DecisionPackage/DecisionFoundViolationsModule/components/DecisionRepeatViolation/ViolationItem';
import {
  getDecisionDataDecisionId,
  getDecisionDataResigningRequired,
  getKnmIsDecisionStatusFillInProcess,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { convertIKnmErknmFormViolatedRequirementsNpaToIKnmErknmFormRequirementNpa } from 'KnmErknmPackage/MainModule/common/convertIKnmErknmFormViolatedRequirementsNpaToIKnmErknmFormRequirementNpa';
import { getKnmDataErpId, getKnmDataResigningRequired } from 'KnmPackage/MainModule/selectors/knmSelectors';

import styles from './ErknmActViolationItems.module.scss';
import { ErknmActRepeatViolationItem } from './ErknmActRepeatViolationItem';
import { useShowMore } from '../../../hooks/useShowMore';

interface IErknmActViolationItemsProps {
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  name: string;
  isAddEnabled: boolean;
  watchOnly: boolean;
  inAct?: boolean;
  isPaperless?: boolean;
  mId: string;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  isDisabledByDirty?: boolean;
  isDisabledByRepeatCheck?: boolean;
  requirementNpa?: IKnmErknmFormViolatedRequirementsNpa | undefined;
}

interface IErknmActViolationItemsStoreProps {
  settings: IAdminSetting[] | undefined;
  isDecisionStatusFillInProcess: boolean;
  resigningRequired: boolean;
  erpId?: string;
}

const negativeSelectOption = booleanSelectOptions.find(option => !option.value);

const ErknmActViolationItemsWithoutStore = ({
  name,
  isAddEnabled,
  watchOnly,
  formRef,
  settings,
  inAct,
  isPaperless,
  mId,
  isKnm,
  isPm,
  isDecision,
  isDisabledByDirty = false,
  isDisabledByRepeatCheck = false,
  isDecisionStatusFillInProcess,
  requirementNpa,
  resigningRequired,
  erpId,
}: IErknmActViolationItemsProps & IErknmActViolationItemsStoreProps) => {
  const violationsFieldName = `${name}.violations`;
  const violationsId = `${mId}.violations`;
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const actFieldName = name.split('.violatedMandatoryRequirements').shift();
  const {
    input: { value: act },
  } = useField(actFieldName as keyof IKnmForm);

  const handleAddViolation = useCallback(() => {
    // @ts-ignore
    const violations = formRef?.current.getFieldState(violationsFieldName).value;

    const newViolation: Partial<IKnmErknmFormViolation> = {
      number: (violations || []).length + 1,
      isNew: true,
      isFixedOnPlace: negativeSelectOption,
      isUnfixable: negativeSelectOption,
      includeInSubject: true,
    };

    push && push(violationsFieldName, newViolation);
  }, []);
  const getRemoveViolationHandler = useCallback(
    index => () => {
      remove && remove(violationsFieldName, index);
    },
    [],
  );

  const created = formRef?.current.getState().values.created;
  const isLaterThanViolationsInProd = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATION_4_1_29_STARTS_FROM,
  );

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const violationResolvedOptions = booleanSelectOptions.map(item => {
    if (item.id === 0) {
      return {
        ...item,
        byDefault: true,
      };
    }

    return item;
  });

  /**
   * Конвертированное нарушение
   */
  const convertedViolation = useMemo(() => {
    if (!requirementNpa) return undefined;

    return convertIKnmErknmFormViolatedRequirementsNpaToIKnmErknmFormRequirementNpa([requirementNpa])?.[0];
  }, [requirementNpa]);

  const renderComponent = useCallback(
    (violationValue: IKnmErknmFormViolation, violationFieldNameName: string, index: number) => {
      if (isDisabledByRepeatCheck) {
        if (isDecision) {
          return (
            <ViolationItem
              violation={violationValue}
              requirementNpa={convertedViolation}
              violationName={violationFieldNameName}
              checkboxDisabled={!isDecisionStatusFillInProcess}
              formRef={formRef as IDecisionFormRef}
              hasUnsignedAct={act && !(act as IKnmErknmFormAct)?.document}
              id={`${mId}.violations[${index}]`}
              currentErpId={erpId}
              marginBottom
            />
          );
        }

        return (
          <ErknmActRepeatViolationItem
            violation={violationValue}
            fieldName={violationFieldNameName}
            formRef={formRef}
            isPaperless={!!isPaperless}
            id={`${mId}.violations[${index}]`}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
            isDisabledByDirty={isDisabledByDirty}
            watchOnly={watchOnly}
            resigningRequired={resigningRequired}
            currentErpId={erpId}
            isActFirstSigned={!!(act as IKnmErknmFormAct)?.document}
            hasUnsignedAct={act && !(act as IKnmErknmFormAct)?.document}
          />
        );
      }

      if (violationValue.isNew) {
        return (
          <ErknmActViolationInitItem
            violation={violationValue}
            fieldName={violationFieldNameName}
            formRef={formRef}
            onRemove={getRemoveViolationHandler(index)}
            isPaperless={!!isPaperless}
            id={`${mId}.violations[${index}]`}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
          />
        );
      }

      return (
        <ErknmActViolationItem
          violation={violationValue}
          fieldName={violationFieldNameName}
          formRef={formRef}
          watchOnly={watchOnly}
          isDisabledByDirty={isDisabledByDirty}
          isPaperless={!!isPaperless}
          isEnablePaperlessDocument={isEnablePaperlessDocument}
          violationResolvedOptions={violationResolvedOptions}
          id={`${mId}.violations[${index}]`}
          isActFirstSigned={!!(act as IKnmErknmFormAct)?.document}
          hasUnsignedAct={act && !(act as IKnmErknmFormAct)?.document}
          isKnm={isKnm}
          isPm={isPm}
          isDecision={isDecision}
        />
      );
    },
    [
      isDisabledByRepeatCheck,
      formRef,
      getRemoveViolationHandler,
      isPaperless,
      mId,
      isKnm,
      isPm,
      isDecision,
      watchOnly,
      isDisabledByDirty,
      violationResolvedOptions,
      act,
      isDecisionStatusFillInProcess,
    ],
  );

  if (!isLaterThanViolationsInProd) {
    return null;
  }

  return (
    <>
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Нарушения',
          field: violationsFieldName,
          isPm,
          isKnm,
          isDecision,
        })}
        id={isDecision ? violationsFieldName : violationsId}
        button={
          isAddEnabled && !isDisabledByRepeatCheck ? (
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              disabled={isDisabledByDirty}
              onClick={handleAddViolation}
            />
          ) : undefined
        }
      />
      {inAct && (
        <div className={styles.HelpText}>
          <Hint text="Каждое нарушение должно быть добавлено отдельно" />
        </div>
      )}
      <FieldArray name={violationsFieldName}>
        {({ fields, meta }) => {
          const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(fields.value, {
            initialCount: 10,
            incrementCount: 10,
          });

          return (
            <>
              {visibleItems.map((violationValue: IKnmErknmFormViolation) => {
                const index = fields.value?.findIndex((item: IKnmErknmFormViolation) => item === violationValue) ?? 0;
                const violationFieldNameName = `${violationsFieldName}[${index}]`;

                return (
                  <Fragment key={violationFieldNameName}>
                    {renderComponent(violationValue, violationFieldNameName, index)}
                  </Fragment>
                );
              })}
              {isRendering && <Spinner className={styles.Loader} />}
              {hasMore && !isRendering && (
                <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
              )}
              {typeof meta.error === 'string' && meta.touched && (
                <p className="shared-input-error shared-row__small-margin">{meta.error}</p>
              )}
            </>
          );
        }}
      </FieldArray>
    </>
  );
};

const mapStateToProps = (
  state: IRootState,
  ownProps: IErknmActViolationItemsProps,
): IErknmActViolationItemsStoreProps => ({
  settings: getAdminFetchSettings(state),
  isDecisionStatusFillInProcess: getKnmIsDecisionStatusFillInProcess(state),
  resigningRequired: ownProps.isDecision ? getDecisionDataResigningRequired(state) : getKnmDataResigningRequired(state),
  erpId: ownProps.isDecision ? getDecisionDataDecisionId(state) : getKnmDataErpId(state),
});

export const ErknmActViolationItems = connect(mapStateToProps)(ErknmActViolationItemsWithoutStore);
